import type { AxiosError } from 'axios';
import { Button, ColorInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Team } from '@/api/generated/models';
import {
  getGetDepartmentQueryKey,
  getListTeamsQueryKey,
  useCreateTeam,
  useListDepartments,
  useUpdateTeam
} from '@/api/generated/departments-teams/departments-teams';
import {
  FormModal,
  ModalFooter,
  MutationButton,
  SelectField,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';

const DEFAULT_TEAM_COLOUR = '#378ADD';
const TEAM_COLOUR_SWATCHES = ['#007AFF', '#34C759', '#FFCC00', '#FF3B30', '#AF52DE'];

const schema = z.object({
  departmentId: z.string().min(1),
  name: z.string().min(1),
  description: z.string().optional(),
  colour: z.string().min(1)
});
type FormValues = z.infer<typeof schema>;

const teamErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface TeamFormModalProps {
  opened: boolean;
  onClose: () => void;
  defaultDepartmentId: string;
  team?: Team;
  onSaved?: (message: string) => void;
}

/** ODJEL je promjenjiv SAMO kod kreiranja — backend nema endpoint za
 * premještanje tima u drugi odjel nakon kreiranja (PATCH ide preko fiksnog
 * department-id iz URL-a), pa je select onemogućen u edit načinu. */
export const TeamFormModal = ({
  opened,
  onClose,
  defaultDepartmentId,
  team,
  onSaved
}: TeamFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!team;

  const { data: departmentsData } = useListDepartments();
  const departmentOptions = (departmentsData ?? [])
    .filter((department) => department.id)
    .map((department) => ({ value: department.id as string, label: department.name ?? '' }));

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: {
      departmentId: defaultDepartmentId,
      name: '',
      description: '',
      colour: DEFAULT_TEAM_COLOUR
    }
  });

  useEffect(() => {
    if (opened) {
      form.setValues(
        team
          ? {
              departmentId: team.departmentId ?? defaultDepartmentId,
              name: team.name ?? '',
              description: team.description ?? '',
              colour: team.colour ?? DEFAULT_TEAM_COLOUR
            }
          : {
              departmentId: defaultDepartmentId,
              name: '',
              description: '',
              colour: DEFAULT_TEAM_COLOUR
            }
      );
    }
  }, [opened, team, defaultDepartmentId]);

  const invalidate = (departmentId: string) => {
    queryClient.invalidateQueries({ queryKey: getListTeamsQueryKey(departmentId) });
    queryClient.invalidateQueries({ queryKey: getGetDepartmentQueryKey(departmentId) });
  };

  const createMutation = useCreateTeam({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (_data, variables) => {
        invalidate(variables.id);
        onSaved?.(t('departments.team.createSuccess'));
        onClose();
      }
    }
  });

  const updateMutation = useUpdateTeam({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (_data, variables) => {
        invalidate(variables.id);
        onSaved?.(t('departments.team.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && team?.id && team.departmentId) {
      updateMutation.mutate({
        id: team.departmentId,
        teamId: team.id,
        data: { name: values.name, colour: values.colour, description: values.description }
      });
    } else {
      createMutation.mutate({
        id: values.departmentId,
        data: { name: values.name, colour: values.colour, description: values.description }
      });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('departments.team.editTeam') : t('departments.team.newTeam')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {mutation.isError && (
            <Toast tone="danger">{teamErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <SelectField
            label={t('departments.team.fieldDepartment')}
            required
            disabled={isEdit}
            allowDeselect={false}
            data={departmentOptions}
            {...form.getInputProps('departmentId')}
          />

          <TextField
            label={t('departments.team.fieldName')}
            required
            {...form.getInputProps('name')}
          />

          <TextareaField
            label={t('departments.team.fieldDescription')}
            minRows={3}
            placeholder={t('departments.team.descriptionPlaceholder')}
            {...form.getInputProps('description')}
          />

          <Stack gap={6}>
            <Text
              fz={11}
              fw={600}
              tt="uppercase"
              style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
            >
              {t('departments.team.fieldColour')}
            </Text>
            <ColorInput
              format="hex"
              swatches={TEAM_COLOUR_SWATCHES}
              swatchesPerRow={5}
              styles={{
                input: {
                  height: 'var(--button-height-default)',
                  borderRadius: 6,
                  backgroundColor: 'var(--color-bg-surface)',
                  border: '1px solid var(--color-border-default)'
                }
              }}
              {...form.getInputProps('colour')}
            />
          </Stack>

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('common.save')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
